import Decimal from 'decimal.js';
import { compact } from 'lodash';

import { CurrencyDollarIcon } from '@/components/icons/CurrencyDollarIcon';
import { User03Icon } from '@/components/icons/User03Icon';
import { Wallet03Icon } from '@/components/icons/Wallet03Icon';
import { getCustodialQualifiedTuitionPersonalAccountSummaryProperties } from '@/modules/entities/details/accounts/CustodialQualifiedTuitionPersonalAccountDetails.utils';
import { getIndividualPersonalAccountSummaryProperties } from '@/modules/entities/details/accounts/IndividualPersonalAccountDetails.utils';
import { getInsuranceAccountSummaryProperties } from '@/modules/entities/details/accounts/InsuranceAccountDetails.utils';
import { getJointPersonalAccountSummaryProperties } from '@/modules/entities/details/accounts/JointPersonalAccountDetails.utils';
import { getRetirementPersonalAccountSummaryProperties } from '@/modules/entities/details/accounts/RetirementPersonalAccountDetails.utils';
import { EntityDetail_EntityFragment } from '@/modules/entities/details/graphql/EntityDetailPage.generated';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { PersonalAccountSummaryData } from '@/modules/personalAccounts/PersonalAccountDetails/PersonalAccountDetails.types';
import { EntityKind } from '@/types/schema';
import { UnreachableError } from '@/utils/errors';
import { formatCurrency } from '@/utils/formatting/currency';

import {
  EntityCoverSlideDataPoint,
  EntityCoverSlideDetailsList,
} from './EntityCoverSlide.types';
import {
  dataPointGetters,
  detailsListGetters,
  getTotalDeathBenefit,
} from './EntityCoverSlide.utils';

export function getPersonalAccountDetails(
  entity: EntityDetail_EntityFragment,
  entityKind:
    | EntityKind.IndividualPersonalAccount
    | EntityKind.JointPersonalAccount
    | EntityKind.InsurancePersonalAccount
    | EntityKind.QualifiedTuitionPersonalAccount
    | EntityKind.RetirementPersonalAccount
    | EntityKind.CustodialPersonalAccount
) {
  let summary: PersonalAccountSummaryData | null = null;

  switch (entityKind) {
    case EntityKind.IndividualPersonalAccount:
      summary = getIndividualPersonalAccountSummaryProperties({
        entity,
        entityType: getEntityTypeFromEntityKind(entityKind),
      });
      break;

    case EntityKind.JointPersonalAccount:
      summary = getJointPersonalAccountSummaryProperties({
        entity,
        entityType: getEntityTypeFromEntityKind(entityKind),
      });
      break;

    case EntityKind.InsurancePersonalAccount:
      summary = getInsuranceAccountSummaryProperties({
        entity,
        entityType: getEntityTypeFromEntityKind(entityKind),
      });
      break;
    case EntityKind.RetirementPersonalAccount:
      summary = getRetirementPersonalAccountSummaryProperties({
        entity,
        entityType: getEntityTypeFromEntityKind(entityKind),
      });
      break;
    case EntityKind.QualifiedTuitionPersonalAccount:
    case EntityKind.CustodialPersonalAccount:
      summary = getCustodialQualifiedTuitionPersonalAccountSummaryProperties({
        entity,
        entityType: getEntityTypeFromEntityKind(entityKind),
      });
      break;

    default: {
      throw new UnreachableError({
        case: entityKind,
        message: `Unhandled personal account subtype: ${entity.subtype.__typename}`,
      });
    }
  }

  if (!summary) {
    return {
      dataPoints: [],
      detailsLists: [],
    };
  }

  if (entityKind === EntityKind.InsurancePersonalAccount) {
    return getInsuranceAccountCoverSlideDetails(entity, summary);
  }

  const dataPoints: EntityCoverSlideDataPoint[] = compact([
    dataPointGetters.getTotalMarketValueDataPoint(entity),
    dataPointGetters.getAssetLocationDataPoint(summary),
    dataPointGetters.getNamesDataPoint(summary),
    dataPointGetters.getTaxStatusDataPoint(summary),
    getAccountTypeDataPoint(summary),
    dataPointGetters.getEffectiveDateDataPoint(summary),
  ]);
  const detailsLists: EntityCoverSlideDetailsList[] = compact([
    detailsListGetters.getControllingPersons(summary),
  ]);

  return {
    dataPoints,
    detailsLists,
    totalDeathBenefit: getTotalDeathBenefit(summary),
  };
}

function getInsuranceAccountCoverSlideDetails(
  entity: EntityDetail_EntityFragment,
  summary: PersonalAccountSummaryData
) {
  const totalDeathBenefit = getTotalDeathBenefit(summary);

  const dataPoints: EntityCoverSlideDataPoint[] = compact([
    getAccountOwnerDataPoint(summary),
    dataPointGetters.getTotalMarketValueDataPoint(entity),
    getTotalDeathBenefitDataPoint(totalDeathBenefit),
  ]);
  const detailsLists: EntityCoverSlideDetailsList[] = compact([
    detailsListGetters.getControllingPersons(summary),
  ]);

  return {
    dataPoints,
    detailsLists,
  };
}

function getAccountOwnerDataPoint(summary: {
  ownerNamesShort?: string | null | undefined;
}): EntityCoverSlideDataPoint | null {
  if (!summary.ownerNamesShort) return null;
  return {
    Icon: User03Icon,
    label: 'Account owner',
    value: summary.ownerNamesShort,
  };
}

function getTotalDeathBenefitDataPoint(
  totalDeathBenefit: Decimal
): EntityCoverSlideDataPoint | null {
  if (totalDeathBenefit.lessThanOrEqualTo(0)) return null;
  return {
    Icon: CurrencyDollarIcon,
    label: 'Total death benefit',
    value: formatCurrency(totalDeathBenefit),
  };
}

function getAccountTypeDataPoint(summary: {
  accountTypeSimple?: string | undefined;
  accountType?: string | undefined;
}): EntityCoverSlideDataPoint | null {
  return summary.accountTypeSimple
    ? {
        Icon: Wallet03Icon,
        label: 'Account type',
        value: summary.accountTypeSimple,
      }
    : null;
}
